<template>
    <Handle type="target" :position="Position.Left" id="input" />
    <NodeBase :title="nodeTitle" icon="⬡" color="#0f766e">

        <!-- no templates exist yet -->
        <template v-if="templatePages.length === 0">
            <p class="text-gray-400 text-[10px] mb-1.5 italic">No template pages in design.</p>
            <template v-if="!pendingCreate">
                <button class="w-full py-1 rounded text-[10px] text-white transition-colors"
                    style="background:var(--ui-active);border:1px solid rgba(255,255,255,0.3)"
                    @click="pendingCreate = true">
                    + Create "{{ suggestedName }}"
                </button>
            </template>
            <template v-else>
                <label>Target page</label>
                <select v-model="createTargetPage" class="mb-1">
                    <option :value="null" disabled>— select —</option>
                    <option v-for="p in pages" :key="p.id" :value="p.id">{{ p.label }}</option>
                </select>
                <div class="flex gap-1">
                    <button class="flex-1 py-1 rounded text-[10px] text-white transition-colors"
                        style="background:var(--ui-active)"
                        :disabled="createTargetPage === null"
                        @click="confirmCreate">Create</button>
                    <button class="px-2 py-1 rounded text-[10px] text-gray-400"
                        style="background:rgba(255,255,255,0.08)"
                        @click="pendingCreate = false">✕</button>
                </div>
            </template>
        </template>

        <!-- templates available -->
        <template v-else>
            <label>Template page</label>
            <select :value="props.data.templatePageId ?? ''"
                @change="e => updateNodeData(props.id, { ...props.data, templatePageId: parseId(e.target.value), templateObjectId: '' })">
                <option value="">-- select template --</option>
                <option v-for="t in templatePages" :key="t.id" :value="t.id">
                    {{ t.label }}
                </option>
            </select>

            <!-- object picker -->
            <template v-if="selected">
                <label class="mt-1.5">Object</label>
                <select :value="props.data.templateObjectId ?? ''"
                    @change="update('templateObjectId', parseId($event.target.value))">
                    <option value="">All objects ({{ selected.objectCount }})</option>
                    <option v-for="o in pageObjects" :key="o.id" :value="o.id">
                        {{ o.label }}
                    </option>
                </select>
                <div class="mt-1 flex items-center gap-1.5">
                    <span class="px-1.5 py-0.5 rounded text-[10px] bg-teal-900 text-teal-300 border border-teal-700">
                        {{ selectedObjectLabel }}
                    </span>
                    <span class="text-gray-500 text-[10px]">→ jsonl at compile</span>
                </div>
            </template>

            <!-- selected page missing warning -->
            <p v-if="props.data.templatePageId !== '' && props.data.templatePageId !== undefined && !selected"
                class="text-amber-400 text-[10px] mt-1">
                ⚠ Template page not found in design
            </p>

            <!-- create new -->
            <template v-if="!pendingCreate">
                <button class="w-full py-1 mt-1.5 rounded text-[10px] text-teal-300 border border-teal-800 hover:bg-teal-900/40 transition-colors"
                    @click="pendingCreate = true">
                    + Create new template page
                </button>
            </template>
            <template v-else>
                <label class="mt-1.5">Target page</label>
                <select v-model="createTargetPage" class="mb-1">
                    <option :value="null" disabled>— select —</option>
                    <option v-for="p in pages" :key="p.id" :value="p.id">{{ p.label }}</option>
                </select>
                <div class="flex gap-1">
                    <button class="flex-1 py-1 rounded text-[10px] text-white transition-colors"
                        style="background:var(--ui-active)"
                        :disabled="createTargetPage === null"
                        @click="confirmCreate">Create</button>
                    <button class="px-2 py-1 rounded text-[10px] text-gray-400"
                        style="background:rgba(255,255,255,0.08)"
                        @click="pendingCreate = false">✕</button>
                </div>
            </template>
        </template>

    </NodeBase>
    <Handle type="source" :position="Position.Right" id="output" />
</template>

<script setup>
defineOptions({ inheritAttrs: false });
import { inject, computed, ref } from 'vue';
import { Handle, Position, useVueFlow } from '@vue-flow/core';
import NodeBase from './NodeBase.vue';

const props = defineProps({ id: String, data: Object });
const { updateNodeData } = useVueFlow();

const haspObjects        = inject('haspObjects', null);
const activeScript       = inject('activeScript', null);
const createTemplatePage = inject('createTemplatePage', null);

const pendingCreate    = ref(false);
const createTargetPage = ref(null);

const pages = computed(() => haspObjects?.pages?.value ?? []);

const update   = (key, val) => updateNodeData(props.id, { ...props.data, [key]: val });
const parseId  = v => (v === '' ? '' : parseInt(v));

const templatePages = computed(() => haspObjects?.templatePages?.value ?? []);

const selected = computed(() =>
    templatePages.value.find(t => t.id === props.data?.templatePageId) ?? null
);

const pageObjects = computed(() => {
    if (!selected.value || !haspObjects) return [];
    return haspObjects.objectsOnPage(selected.value.id);
});

const selectedObject = computed(() =>
    pageObjects.value.find(o => o.id === props.data?.templateObjectId) ?? null
);

const selectedObjectLabel = computed(() => {
    if (selectedObject.value) return selectedObject.value.label;
    return `${selected.value?.objectCount ?? 0} object${(selected.value?.objectCount ?? 0) !== 1 ? 's' : ''}`;
});

const nodeTitle = computed(() =>
    selected.value
        ? (selectedObject.value ? `${selected.value.label} · ${selectedObject.value.obj}` : selected.value.label)
        : 'Template Ref'
);

// Default name suggestion based on the active script tab
const suggestedName = computed(() => {
    const script = activeScript?.value ?? 'script';
    return `Template ${script}`;
});

function confirmCreate() {
    if (!createTemplatePage || createTargetPage.value === null) return;
    const newId = createTemplatePage(suggestedName.value, createTargetPage.value);
    if (newId !== null) {
        update('templatePageId', newId);
    }
    pendingCreate.value    = false;
    createTargetPage.value = null;
}
</script>
